import { activeExtras, themeById, describeService } from "../../../lib/site-draft"
import { slugify } from "../../../lib/slug"
import { tradeBySlug } from "../../../data/trades"
import { formatPhone, telHref } from "../../../lib/phone"
import { buildTheme, tokensToStyle } from "../../../lib/template-theme"

/**
 * The builder's draft, in the shape the Mockup Maker's templates expect —
 * MTC-FCS-041.
 * ============================================================================
 * Their designs read a view model (`v`) rather than raw form data, and every
 * element in them is gated on the fact it states actually existing. That is
 * what makes the port work despite this builder collecting less: a contractor
 * who never typed a review score simply gets a page with no review band, which
 * is exactly how the design behaves in the maker with that field empty.
 *
 * ── NOTHING IS INVENTED ────────────────────────────────────────────────────
 * Every field below is either something the contractor typed or something
 * derived from it. No placeholder ratings, no made-up years in business, no
 * stand-in address. A template that fills gaps with plausible fiction is a
 * template that publishes a lie on somebody's business website.
 */

const clean = value => String(value ?? "").trim()

/**
 * Trims to a whole word and adds an ellipsis only if something was cut.
 *
 * A hard `slice` ends sentences mid-word, and a page that says "…how long y"
 * looks broken rather than summarised — on somebody's actual website.
 */
function shorten(text, max) {
  const value = clean(text)
  if (value.length <= max) return value
  const cut = value.slice(0, max)
  const lastSpace = cut.lastIndexOf(" ")
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[.,;:\s]+$/, "")}…`
}

/** "Ridge Line Roofing" → "RL". Two letters, because three looks like an acronym. */
function initialsFor(name) {
  const words = clean(name)
    .split(/\s+/)
    .filter(word => /[A-Za-z0-9]/.test(word))
  if (words.length === 0) return "FC"
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[words.length - 1][0]).toUpperCase()
}

/**
 * The name, split so the last word can carry the accent colour.
 *
 * "Ridge Line Roofing" → lead "Ridge Line", accent "Roofing". One-word names
 * keep the whole thing as the lead: colouring the only word there is makes the
 * logo look like a mistake.
 */
function splitName(name) {
  const words = clean(name).split(/\s+/).filter(Boolean)
  if (words.length < 2) return { lead: clean(name) || "Your Business", accent: "" }
  return { lead: words.slice(0, -1).join(" "), accent: words[words.length - 1] }
}

/** The service areas as one readable line, or "". */
function areaLineFor(areas) {
  const states = Array.isArray(areas?.states) ? areas.states : []
  if (states.length === 0) return ""
  const whole = Array.isArray(areas?.wholeStates) ? areas.wholeStates : []
  if (states.length === 1) {
    return whole.includes(states[0]) ? `all of ${states[0]}` : states[0]
  }
  if (states.length === 2) return `${states[0]} and ${states[1]}`
  return `${states.slice(0, -1).join(", ")} and ${states[states.length - 1]}`
}

/**
 * Builds the view.
 *
 * @param {object} draft   the builder's draft
 * @param {object} options `plan` overrides the draft's own choice (the publish
 *                         path passes the tier that was actually paid for)
 */
export function buildView(draft, { plan, mode = "local" } = {}) {
  const business = draft.business || {}
  const trade = tradeBySlug(draft.tradeSlug)
  const theme = themeById(draft.themeId)
  const name = clean(business.name) || "Your Business"
  const { lead, accent } = splitName(name)

  const phone = formatPhone(business.phone)
  const email = clean(business.email)
  const areaLine = areaLineFor(draft.areas)

  const services = (draft.services || []).map((title, i) => ({
    slug: `${title}-${i}`.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
    title,
    description: clean(draft.serviceCopy?.[title]) || describeService(title, draft.tradeSlug),
    image: draft.images?.[`service${i + 1}`] || "",
    icon: trade?.icon || "tools",
  }))

  const gallery = Object.keys(draft.images || {})
    .filter(key => key.startsWith("gallery"))
    .sort()
    .map(key => draft.images[key])
    .filter(Boolean)

  return {
    name,
    lead,
    accent,
    initials: initialsFor(name),
    slogan: clean(business.tagline),
    // The trade record calls it `name`; there is no `label` on it, and reading
    // one gave every page "Contractor" where the trade should be.
    trade: trade?.name || "Contractor",
    tradeSlug: draft.tradeSlug || "",
    sloganLead: trade?.name || "The Work",

    phone,
    phoneHref: telHref(business.phone),
    hasPhone: Boolean(phone),
    email,
    emailHref: email ? `mailto:${email}` : "",
    hasEmail: Boolean(email),

    aboutText: clean(business.about),
    // Cut at a word, not at a character: "…how long y" reads as a broken page
    // rather than as a summary.
    aboutShort: shorten(clean(business.about), 165),

    // The areas are the only "where" this builder collects — there is no
    // street address, so anything in the designs that needs one is left out
    // rather than filled with the state name pretending to be a location.
    serviceArea: areaLine,
    areaLine: areaLine ? `Serving ${areaLine}` : "",
    city: areaLine,
    cityIsReal: Boolean(areaLine),

    /* ── The quote form — MTC-FCS-017 ─────────────────────────────────
       `wantsForm` is the Lead Generation tier and the switch being on, read
       through the same gate the Classic design uses, so one answer decides it
       for every template. `siteSlug` is which published website an enquiry
       belongs to: the address chosen at checkout once it exists, and the
       derived one before that so the preview's form points somewhere sensible
       rather than nowhere. */
    wantsForm: activeExtras(draft, plan || draft.plan).leadform,
    siteSlug: draft.slug || slugify(business.name),

    heroImage: draft.images?.hero || "",
    aboutImage: draft.images?.about || "",
    services,
    gallery,
    hasGallery: gallery.length > 0,

    /* ── What a paid plan adds, read through the same gate Classic uses — FCS-124 ──────────────────────────────────────────
       A section is only there when the plan covers it, it is switched on AND the contractor put something in it (activeExtras),
       so a design that draws these never draws an empty heading and never draws anybody's words but theirs. Every field is
       either typed by the contractor or absent: nothing is invented, and an empty list means "do not draw the section". */
    ...(() => {
      const extras = activeExtras(draft, plan || draft.plan)
      return {
        shots: extras.gallery ? (draft.gallery || []).filter(Boolean).slice(0, 9) : [],
        reviews: extras.reviews ? (draft.reviews || []).filter(r => r && clean(r.quote)).slice(0, 6).map(r => ({ quote: clean(r.quote), name: clean(r.name) })) : [],
        faqs: extras.faqs ? (draft.faqs || []).filter(f => f && clean(f.q) && clean(f.a)).slice(0, 8).map(f => ({ q: clean(f.q), a: clean(f.a) })) : [],
        hours: extras.hours ? (draft.hours || []).filter(h => h && clean(h.text)).map(h => ({ day: clean(h.day), text: clean(h.text) })) : [],
      }
    })(),

    year: new Date().getFullYear(),

    // Tokens the template stylesheets read, from the palette the contractor
    // picked in the builder.
    style: tokensToStyle(buildTheme(theme.primary, theme.accent, "light", "v2")),
    plan: plan || draft.plan || "free",

    /* Which source this page is being drawn from — FCS-057(b). A design can
       branch on it; none do today, which is the point: one template renders
       the builder's preview and the live customer site from the same code. */
    mode,
    isRemote: mode === "remote",
  }
}
